import {
  format as formatFns,
  addMilliseconds,
  differenceInMilliseconds,
  isPast,
  isBefore,
  subDays,
  parseISO,
} from 'date-fns';
import type { Locale } from 'date-fns';

// Note: Keep your imports from @/config/constants

// --- CORE WRAPPERS (Keep these for Testing/Mocking) ---

export function now(): number {
  return Date.now();
}

export function currentDate(): Date {
  return new Date();
}

// --- LOGIC HELPERS (Refactored to use date-fns) ---

/**
 * Format a date
 * Improvement: You can now choose between Intl (Native) or date-fns format tokens
 */
export function formatDate(
  date: Date | number,
  formatStr: string = 'PP', // date-fns default pattern (e.g. "Jan 29, 2026")
  locale?: Locale
): string {
  // date-fns automatically handles numbers or Date objects
  return formatFns(date, formatStr, { locale });
}

export function toISODate(date?: Date | number): string {
  if (date === undefined) return currentDate().toISOString();
  // Safe handling if 'date' is a number
  return (typeof date === 'number' ? new Date(date) : date).toISOString();
}

/**
 * Check if a timestamp has expired
 * Improvement: distinct logic using date-fns
 */
export function hasExpired(date: Date | number): boolean {
  // isPast returns true if the date is earlier than now
  // Note: date-fns 'isPast' expects a Date or number, works great here
  return isPast(date);
}

/**
 * Check if a timestamp will expire within a duration
 */
export function willExpireWithin(
  date: Date | number,
  durationMs: number
): boolean {
  const expiryThreshold = addMilliseconds(now(), durationMs);

  // Is the target date BEFORE (now + duration)?
  return isBefore(date, expiryThreshold);
}

/**
 * Add time
 * Improvement: Uses addMilliseconds which returns a NEW date (Immutable)
 */
export function addTime(date: Date | number, durationMs: number): Date {
  return addMilliseconds(date, durationMs);
}

/**
 * Time Difference
 * Improvement: Standardized math
 */
export function timeDifference(
  date1: Date | number,
  date2: Date | number = now()
): number {
  return differenceInMilliseconds(date1, date2);
}

/**
 * Helper for your "30 days ago" requirement
 * New addition to replace your complex one-liner
 */
export function daysAgo(days: number): Date {
  return subDays(currentDate(), days);
}

export function parseISOToTimestamp(isoString: string): number {
  return parseISO(isoString).getTime();
}
